/**
 * PublicSignPage — accessible at /sign/{token} with NO authentication.
 *
 * The token in the URL is the auth (Plaid-style "the link IS the key"). When
 * the page loads we fetch the request payload (PDF + field positions), render
 * the PDF in an iframe, overlay live input controls at each field position,
 * and POST the filled answers back to the backend on Submit.
 *
 * Signature fields use a small HTML5 canvas-based "draw your signature" pad
 * (no extra deps). Date fields auto-populate today's date but are editable.
 * Text fields are plain inputs.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileSignature, Loader2, Check, Send, Pen, X, ChevronLeft, ChevronRight, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { usePdfDocument, PdfPageCanvas } from '../components/signatures/PdfPageCanvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Signature pad (lightweight, no deps) ───────────────────────────────────

function SignaturePad({ value, onChange, onClose }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const ptFromEvent = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const cx = (t ? t.clientX : e.clientX) - rect.left;
    const cy = (t ? t.clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current = ptFromEvent(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = ptFromEvent(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const save = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChange(dataUrl);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Draw your signature</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close"><X className="w-4 h-4" /></Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            className="w-full border-2 border-dashed border-emerald-300 rounded bg-white cursor-crosshair touch-none"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            data-testid="signature-canvas"
          />
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={clear}>Clear</Button>
            <Button onClick={save} className="gap-1.5" data-testid="signature-save-btn">
              <Check className="w-4 h-4" /> Use this signature
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Field overlay (renders editable input on top of the PDF) ───────────────

// Defensive base styling — applied to EVERY field type so chrome/borders
// don't bleed onto neighbors on narrow viewports.
const FIELD_BASE_CLS =
  "box-border overflow-hidden focus-within:z-10";

// iOS Safari auto-zooms when a focused input has font-size < 16px.
// We use 16px in the style attribute below to suppress that and avoid the
// layout jump that makes fields appear to overlap.

function FieldInput({ field, value, onChange, openPad, isFlashing, isNext }) {
  const baseStyle = {
    position: 'absolute',
    left: `${field.x * 100}%`,
    top: `${field.y * 100}%`,
    width: `${field.width * 100}%`,
    height: `${field.height * 100}%`,
    boxSizing: 'border-box',
    overflow: 'hidden',
    // Avoid iOS focus-zoom that causes visual overlap on narrow viewports.
    fontSize: '16px',
  };
  const stateCls = `${isFlashing ? 'sign-field-flash' : ''} ${isNext ? 'sign-field-next' : ''}`.trim();
  if (field.type === 'signature') {
    return (
      <button
        type="button"
        id={`sign-field-${field.field_id}`}
        onClick={() => openPad(field.field_id)}
        style={baseStyle}
        className={`${FIELD_BASE_CLS} ${stateCls} flex items-center justify-center border-2 rounded font-medium ${value
          ? 'border-emerald-500 bg-emerald-50/95 dark:bg-emerald-900/40'
          : 'border-amber-400 bg-amber-50/95 dark:bg-amber-900/40 border-dashed'}`}
        data-testid={`field-signature-${field.field_id}`}
      >
        {value ? (
          <img src={value} alt="signature" className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-amber-700 dark:text-amber-300 flex items-center gap-1 text-[11px] leading-none">
            <Pen className="w-3 h-3 flex-shrink-0" /> Sign here
          </span>
        )}
      </button>
    );
  }
  // Sender-provided default text on a TEXT field — render as read-only
  // (the sender has locked this value at placement time).
  if (field.type === 'text' && (field.default_value || '').trim()) {
    return (
      <div
        style={baseStyle}
        className={`${FIELD_BASE_CLS} border-2 border-emerald-500 bg-emerald-50/95 dark:bg-emerald-900/40 rounded px-1 flex items-center text-emerald-900 dark:text-emerald-100 select-text text-[11px] leading-none`}
        data-testid={`field-${field.type}-${field.field_id}`}
        title="This value was pre-filled by the sender"
      >
        <span className="truncate w-full">{field.default_value}</span>
      </div>
    );
  }
  // For date fields on mobile, hide the native calendar icon — it pushes
  // chrome outside the bounding box and overlaps adjacent fields.
  const isDate = field.type === 'date';
  return (
    <input
      type={isDate ? 'date' : 'text'}
      id={`sign-field-${field.field_id}`}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={isDate ? 'YYYY-MM-DD' : (field.label || 'Type here')}
      style={{
        ...baseStyle,
        // Strip the iOS/Android default date-input chrome.
        WebkitAppearance: 'none',
        appearance: 'none',
      }}
      className={`${FIELD_BASE_CLS} ${stateCls} border-2 border-amber-400 bg-white/95 dark:bg-zinc-900/95 rounded px-1 text-[11px] leading-none focus:border-emerald-500 focus:outline-none`}
      data-testid={`field-${field.type}-${field.field_id}`}
    />
  );
}

// Read-only field already completed by a PREVIOUS signer (multi-signer flow).
function CompletedField({ field }) {
  const style = {
    position: 'absolute',
    left: `${field.x * 100}%`,
    top: `${field.y * 100}%`,
    width: `${field.width * 100}%`,
    height: `${field.height * 100}%`,
    boxSizing: 'border-box',
    overflow: 'hidden',
  };
  const isSig = field.type === 'signature' && (field.value || '').startsWith('data:image');
  return (
    <div
      style={style}
      className={`${FIELD_BASE_CLS} border border-zinc-300 dark:border-zinc-700 bg-zinc-50/90 dark:bg-zinc-800/70 rounded flex items-center px-1 select-none`}
      title={`Signed by ${field.signer_name || 'a previous signer'}`}
      data-testid={`completed-field-${field.field_id}`}
    >
      {isSig ? (
        <img src={field.value} alt={`Signature of ${field.signer_name || 'previous signer'}`} className="max-h-full max-w-full object-contain" />
      ) : (
        <span className="truncate w-full text-[11px] leading-none text-zinc-700 dark:text-zinc-200">{field.value}</span>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function PublicSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [signerName, setSignerName] = useState('');
  const [padFieldId, setPadFieldId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');
  // P2: signer self-decline dialog state
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);
  const { pdf, numPages } = usePdfDocument(request?.pdf_data_uri || null);
  // Page navigation state (sticky toolbar): tracks the page currently in
  // view + a typeable jump input. Refs keep DOM nodes for programmatic scroll.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [flashFieldId, setFlashFieldId] = useState(null);
  const pageRefs = useRef({});
  const toolbarRef = useRef(null);

  // Fields this signer must still fill, ordered top-to-bottom, page-by-page.
  // Excludes sender-locked TEXT defaults (rendered read-only) and any
  // already-answered required fields.
  const actionableFields = useMemo(() => {
    if (!request?.fields) return [];
    return request.fields
      .filter((f) => {
        if (f.type === 'text' && (f.default_value || '').trim()) return false;
        return f.required !== false;
      })
      .slice()
      .sort((a, b) => {
        const pa = a.page || 1;
        const pb = b.page || 1;
        if (pa !== pb) return pa - pb;
        if (a.y !== b.y) return a.y - b.y;
        return a.x - b.x;
      });
  }, [request]);

  const unfilledFields = useMemo(
    () => actionableFields.filter((f) => !answers[f.field_id]),
    [actionableFields, answers],
  );

  const totalCount = actionableFields.length;
  const remainingCount = unfilledFields.length;
  const filledCount = totalCount - remainingCount;

  // "Up next" target — the field the signer should tackle now. Prefer an
  // unfilled field at-or-after the current page; fall back to the very first
  // unfilled overall. Recomputes automatically as the signer scrolls (which
  // updates currentPage) or fills fields (which shrinks unfilledFields).
  const nextTargetId = useMemo(() => {
    if (!unfilledFields.length) return null;
    const t = unfilledFields.find((f) => (f.page || 1) >= currentPage) || unfilledFields[0];
    return t.field_id;
  }, [unfilledFields, currentPage]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/esign/sign/${token}`);
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || 'Invalid or expired link');
        }
        const data = await res.json();
        setRequest(data);
        setSignerName(data.recipient_name || '');
        // Auto-fill: date fields → today; text fields → signer's name (only
        // when the sender did NOT lock a custom default_value).
        const today = new Date().toISOString().slice(0, 10);
        const init = {};
        for (const f of data.fields || []) {
          if (f.type === 'date') init[f.field_id] = today;
          else if (f.type === 'text' && !(f.default_value || '').trim()) {
            init[f.field_id] = data.recipient_name || '';
          }
        }
        setAnswers(init);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setAnswer = (fid, val) => setAnswers(prev => ({ ...prev, [fid]: val }));

  // Compute offset of sticky headers so scrollTo lands the target below them.
  const stickyOffset = () => {
    const header = document.querySelector('[data-role="sign-header"]');
    const h1 = header?.offsetHeight || 0;
    const h2 = toolbarRef.current?.offsetHeight || 0;
    return h1 + h2 + 12;
  };

  const scrollToPage = (p) => {
    const el = pageRefs.current[p];
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset();
    window.scrollTo({ top, behavior: 'smooth' });
    setCurrentPage(p);
    setPageInput(String(p));
  };

  const scrollToField = (field) => {
    const el = document.getElementById(`sign-field-${field.field_id}`);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset() - 40;
    window.scrollTo({ top, behavior: 'smooth' });
    setCurrentPage(field.page || 1);
    setPageInput(String(field.page || 1));
    setFlashFieldId(field.field_id);
    // Focus non-signature inputs so the signer can type immediately.
    if (field.type !== 'signature') {
      setTimeout(() => { try { el.focus({ preventScroll: true }); } catch { /* noop */ } }, 350);
    }
    setTimeout(() => setFlashFieldId(null), 1600);
  };

  const goToNextField = () => {
    if (!unfilledFields.length) return;
    // Prefer next unfilled AFTER the currently-viewed page; else wrap to first.
    const next = unfilledFields.find((f) => (f.page || 1) >= currentPage) || unfilledFields[0];
    scrollToField(next);
  };

  const handlePageInputCommit = () => {
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n) && n >= 1 && n <= numPages) scrollToPage(n);
    else setPageInput(String(currentPage));
  };

  // Track which page is in view by observing page wrappers.
  useEffect(() => {
    if (!numPages) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const p = Number(visible[0].target.getAttribute('data-page'));
          if (p) {
            setCurrentPage(p);
            setPageInput(String(p));
          }
        }
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    Object.values(pageRefs.current).forEach((el) => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [numPages, pdf]);

  // Publish the sign-header height as a CSS var so the toolbar sticks
  // immediately below it (header height varies by viewport width).
  useEffect(() => {
    const header = document.querySelector('[data-role="sign-header"]');
    if (!header) return undefined;
    const publish = () => {
      const h = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--sign-header-h', `${Math.round(h)}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(header);
    window.addEventListener('resize', publish);
    return () => { ro.disconnect(); window.removeEventListener('resize', publish); };
  }, [request, pdf]);

  const handleSubmit = async () => {
    // Validate required fields up-front so we don't roundtrip to find missing ones.
    const missing = request.fields.filter(f => f.required && !answers[f.field_id]);
    if (missing.length) {
      toast.error(`Please fill: ${missing.map(f => f.label || f.type).join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/esign/sign/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, signer_name: signerName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to submit');
      if (data.completed === false) setDoneMessage(data.message || '');
      setDone(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Recipient self-decline. Sender is notified server-side; the link is
  // invalidated; the request shows "declined" in the sender's list.
  const handleDecline = async () => {
    setDeclining(true);
    try {
      const res = await fetch(`${API_URL}/api/esign/sign/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to decline');
      setDeclined(true);
      setDeclineDialogOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-rose-600">Cannot open document</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-emerald-50/40 dark:bg-emerald-950/20">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Check className="w-6 h-6 text-emerald-600" />
              <CardTitle>Signed!</CardTitle>
            </div>
            <CardDescription data-testid="signed-done-message">
              {doneMessage || `The signed document and an audit certificate have been filed in ${request?.sender_name || 'the sender'}'s account. You're all set — feel free to close this tab.`}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50 dark:bg-zinc-950">
        <Card className="max-w-md w-full" data-testid="declined-confirmation">
          <CardHeader>
            <div className="flex items-center gap-2">
              <X className="w-6 h-6 text-zinc-500" />
              <CardTitle>Declined</CardTitle>
            </div>
            <CardDescription>
              You declined to sign this document. {request?.sender_name || 'The sender'} has been notified. You can close this tab.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Page-scoped CSS to fully neutralize native date-input chrome that
          causes visual overlap on iOS/Android when fields are placed close
          together by the sender. */}
      <style>{`
        .sign-field input[type="date"]::-webkit-calendar-picker-indicator,
        .sign-field input[type="date"]::-webkit-inner-spin-button,
        .sign-field input[type="date"]::-webkit-clear-button {
          display: none;
          -webkit-appearance: none;
          margin: 0;
        }
        .sign-field input[type="date"] { -moz-appearance: textfield; }
        @keyframes signFieldFlash {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
          40%      { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.45), 0 0 0 12px rgba(16, 185, 129, 0.18); }
        }
        .sign-field-flash {
          animation: signFieldFlash 1.5s ease-out;
          outline: 2px solid rgb(16, 185, 129) !important;
          outline-offset: 2px;
        }
        @keyframes signFieldNextPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55); }
          50%      { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.0); }
        }
        .sign-field-next {
          animation: signFieldNextPulse 1.8s ease-in-out infinite;
          outline: 2px solid rgb(16, 185, 129) !important;
          outline-offset: 2px;
          z-index: 5;
        }
        /* When both are active (signer just hit "Next field"), the flash
           wins visually because it's a one-shot with a stronger shadow. */
        .sign-field-flash.sign-field-next { animation: signFieldFlash 1.5s ease-out; }
      `}</style>
      <header data-role="sign-header" className="border-b bg-background/95 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <FileSignature className="w-5 h-5 text-emerald-600" />
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold truncate">{request.subject}</h1>
            <p className="text-xs text-muted-foreground truncate">From {request.sender_name || request.sender_email}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setDeclineDialogOpen(true)}
            disabled={submitting || declining}
            className="gap-1.5 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950/30"
            data-testid="public-decline-btn"
          >
            <X className="w-4 h-4" /> Decline
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="gap-1.5" data-testid="public-sign-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Sign &amp; Submit
          </Button>
        </div>
      </header>

      {/* Sticky navigation toolbar — page jump + next-field walker + progress.
          Only renders once the PDF has loaded so we know numPages / fields. */}
      {pdf && (
        <div
          ref={toolbarRef}
          className="border-b bg-white/95 dark:bg-zinc-900/95 backdrop-blur-sm sticky z-10"
          style={{ top: 'var(--sign-header-h, 56px)' }}
          data-testid="sign-toolbar"
        >
          <div className="max-w-5xl mx-auto px-4 py-2 flex flex-wrap items-center gap-2">
            {numPages > 1 && (
              <div className="flex items-center gap-1.5" data-testid="page-nav-group">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => scrollToPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                  data-testid="page-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-1">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={handlePageInputCommit}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePageInputCommit(); } }}
                    className="h-8 w-14 text-center text-sm px-1"
                    aria-label="Jump to page number"
                    data-testid="page-jump-input"
                  />
                  <span className="text-xs text-muted-foreground">of {numPages}</span>
                </div>
                <Select value={String(currentPage)} onValueChange={(v) => scrollToPage(Number(v))}>
                  <SelectTrigger className="h-8 w-[110px]" data-testid="page-select">
                    <SelectValue placeholder="Page" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
                      <SelectItem key={p} value={String(p)}>Page {p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => scrollToPage(Math.min(numPages, currentPage + 1))}
                  disabled={currentPage >= numPages}
                  aria-label="Next page"
                  data-testid="page-next-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}

            {totalCount > 0 && (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                  remainingCount === 0
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                    : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
                }`}
                data-testid="fields-progress-badge"
              >
                <FileSignature className="w-3 h-3" />
                {remainingCount === 0
                  ? `All ${totalCount} field${totalCount === 1 ? '' : 's'} filled`
                  : `${filledCount} of ${totalCount} filled · ${remainingCount} left`}
              </span>
            )}

            <div className="ml-auto flex items-center gap-2">
              {remainingCount > 0 ? (
                <Button
                  size="sm"
                  onClick={goToNextField}
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="next-field-btn"
                >
                  <ArrowDown className="w-3.5 h-3.5" /> Next field
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="gap-1.5"
                  data-testid="toolbar-submit-btn"
                >
                  {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  All filled — Submit
                </Button>
              )}
            </div>
          </div>

          {/* Legend row — small color guide so first-time signers instantly
              understand what each field state means. Hidden on very narrow
              viewports (sm:flex) to keep the toolbar height reasonable on
              mobile; the color-coding itself is still legible without it. */}
          <div
            className="hidden sm:flex max-w-5xl mx-auto px-4 pb-2 items-center gap-3 flex-wrap"
            data-testid="sign-legend"
          >
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Legend:</span>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="inline-block w-3.5 h-3.5 rounded border-2 border-emerald-500 bg-white sign-field-next" style={{ animationDuration: '2.4s' }} />
              Up next
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="inline-block w-3.5 h-3.5 rounded border-2 border-amber-400 border-dashed bg-amber-50" />
              To fill
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="inline-block w-3.5 h-3.5 rounded border-2 border-emerald-500 bg-emerald-50" />
              Filled
            </span>
            {(request.completed_fields?.length > 0) && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="inline-block w-3.5 h-3.5 rounded border border-zinc-300 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800" />
                Signed by another
              </span>
            )}
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto p-4 space-y-3">
        {request.total_signers > 1 && (
          <div
            className="flex items-center gap-2 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50/70 dark:bg-sky-950/30 px-3 py-2 text-xs text-sky-900 dark:text-sky-200"
            data-testid="multi-signer-banner"
          >
            <FileSignature className="w-3.5 h-3.5 shrink-0" />
            <span>
              You are <b>signer {request.signer_order} of {request.total_signers}</b> on this document.
              {request.completed_fields?.length > 0 && ' Fields already signed by others are shown locked in grey.'}
            </span>
          </div>
        )}
        {request.message && (
          <Card>
            <CardContent className="py-3">
              <p className="text-sm whitespace-pre-line">{request.message}</p>
            </CardContent>
          </Card>
        )}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Your name (for the audit trail)</label>
          <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Type your full name" data-testid="signer-name-input" />
        </div>

        {/* PDF preview + overlay — each page rendered via pdf.js with its own
            field overlay, stacked vertically so multi-page docs scroll naturally. */}
        {!pdf ? (
          <div className="flex items-center justify-center w-full bg-white rounded shadow-sm" style={{ aspectRatio: '612 / 792' }}>
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="space-y-4" data-testid="public-pdf-overlay">
            {Array.from({ length: numPages }, (_, i) => i + 1).map((pn) => (
              <div
                key={pn}
                data-page={pn}
                ref={(el) => { pageRefs.current[pn] = el; }}
              >
                {numPages > 1 && (
                  <p className="text-[11px] text-muted-foreground text-center mb-1">Page {pn} of {numPages}</p>
                )}
                <PdfPageCanvas pdf={pdf} pageNumber={pn} className="shadow-sm">
                  <div className="sign-field absolute inset-0" data-testid={`public-pdf-overlay-page-${pn}`}>
                    {(request.completed_fields || []).filter((f) => (f.page || 1) === pn).map(f => (
                      <CompletedField key={`done-${f.field_id}`} field={f} />
                    ))}
                    {request.fields.filter((f) => (f.page || 1) === pn).map(f => (
                      <FieldInput
                        key={f.field_id}
                        field={f}
                        value={answers[f.field_id]}
                        onChange={(v) => setAnswer(f.field_id, v)}
                        openPad={(fid) => setPadFieldId(fid)}
                        isFlashing={flashFieldId === f.field_id}
                        isNext={nextTargetId === f.field_id}
                      />
                    ))}
                  </div>
                </PdfPageCanvas>
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground text-center pt-2 pb-8">
          By clicking <b>Sign &amp; Submit</b> you agree to be electronically bound under the E-SIGN Act and UETA. Your IP address, browser, and timestamp will be recorded.
        </p>
      </main>

      {padFieldId && (
        <SignaturePad
          value={answers[padFieldId]}
          onChange={(v) => setAnswer(padFieldId, v)}
          onClose={() => setPadFieldId(null)}
        />
      )}

      {/* Signer self-decline dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <X className="w-5 h-5" /> Decline to sign
            </DialogTitle>
            <DialogDescription>
              The sender will be notified. The signing link will stop working after you decline. You can include an optional reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Reason (optional)</label>
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={3}
              maxLength={300}
              placeholder="e.g. The terms aren't acceptable; this should go to my colleague instead."
              data-testid="decline-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeclineDialogOpen(false)}>Never mind</Button>
            <Button
              variant="destructive"
              onClick={handleDecline}
              disabled={declining}
              data-testid="decline-confirm-btn"
            >
              {declining ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <X className="w-3.5 h-3.5 mr-1.5" />}
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
